import {
  formatDistance,
  isMetric,
} from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import {
  PointCloudAnalysis,
  PointCloudAnalysisColormap,
  ReferencePlaneType,
} from "@/store/point-cloud-analysis-tool-slice";
import {
  ColorBar,
  ColorsWithRatio,
  FaroText,
  grey,
  NO_TRANSLATE_CLASS,
} from "@faro-lotv/flat-ui";
import { DistanceUnitTypes } from "@faro-lotv/foundation";
import { Box, Stack } from "@mui/system";
import { useMemo, type JSX } from "react";
import { ColorManagement } from "three";

type ColorScaleProps = {
  /** The Analysis being rendered */
  analysis: PointCloudAnalysis;
  /**
   * If true, the scale will be rendered in dark mode.
   */
  dark?: boolean;
};

/**
 * @returns the color scale bar with the min and max color deviation labels.
 */
export function ColormapScale({
  analysis,
  dark,
}: ColorScaleProps): JSX.Element {
  const colorBarColors = useMemo(
    () => computeColorBarColors(analysis.colormap),
    [analysis.colormap],
  );

  const scaleRange = useMemo(() => {
    if (analysis.referencePlaneType !== ReferencePlaneType.volume) {
      return [-analysis.tolerance, analysis.tolerance];
    }
    if (analysis.volumeAnalysisResult) {
      return [
        analysis.volumeAnalysisResult.heightMin,
        analysis.volumeAnalysisResult.heightMax,
      ];
    }
    return [0, 0];
  }, [
    analysis.referencePlaneType,
    analysis.tolerance,
    analysis.volumeAnalysisResult,
  ]);

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ height: "100%", justifyContent: "center" }}
    >
      <ColorBar colors={colorBarColors} direction="vertical" />

      <ColorScaleLineIndicatorVertical
        maxValue={scaleRange[1]}
        minValue={scaleRange[0]}
        useLargeUnit={analysis.referencePlaneType === ReferencePlaneType.volume}
        dark={dark}
      />
    </Stack>
  );
}

/**
 * @returns the string to be used for one graduated label
 * @param value value to display in meter
 * @param distanceUnit distance unit setting selected by user
 * @param useLargeUnit whether to use large unit (meters or feet) or small unit (millimeters or inches)
 */
function formatLabelText(
  value: number,
  distanceUnit: DistanceUnitTypes,
  useLargeUnit: boolean,
): string {
  const metric = isMetric(distanceUnit);

  return useLargeUnit
    ? formatDistance(
        value,
        metric ? DistanceUnitTypes.meters : DistanceUnitTypes.decimalFeet,
      )
    : formatDistance(
        value,
        metric
          ? DistanceUnitTypes.millimeters
          : DistanceUnitTypes.decimalInches,
      );
}

function computeColorBarColors(
  colormap: PointCloudAnalysisColormap,
): ColorsWithRatio {
  // The whole colormap range is mapped to the middle portion of the color bar.
  // The space before and after the whole range is padding with the minimal and maximal deviation color.
  // This constant defines padding ratio of the space before and after the color map range.
  const COLOR_SCALE_PADDING = 0.15;
  // scale the colormap range to middle portion of the color bar, with padding on both sides
  const colorRange = 1.0 - 2 * COLOR_SCALE_PADDING;

  // Colors supplied to three.js need to be in the Linear-sRGB. Certain conversions (for hexadecimal
  // and CSS colors in sRGB) can be made automatically if the THREE.ColorManagement API is enabled.
  // see: https://threejs.org/docs/?q=color#manual/en/introduction/Color-management
  // Enable the ColorManagement make sure `getHexString` return the hexadecimal color in sRGB for CSS.
  // However, the global flag `ColorManagement.enabled` may be set to `false` by the application for
  // certain reason, so we need to save and restore it.
  const colorManagementEnabled = ColorManagement.enabled;
  ColorManagement.enabled = true;

  const colors: ColorsWithRatio = colormap.map((colorKey) => ({
    color: colorKey.color,
    ratio: colorKey.value * colorRange + COLOR_SCALE_PADDING,
  }));

  ColorManagement.enabled = colorManagementEnabled;

  return [
    {
      color: colors[0].color,
      ratio: 0.0,
    },
    ...colors,
    {
      color: colors[colors.length - 1].color,
      ratio: 1.0,
    },
  ];
}

type VerticalColorScaleLineIndicatorProps = {
  /** Max deviation in meter */
  maxValue: number;

  /** Min deviation in meter */
  minValue: number;

  /** Use large unit for labels */
  useLargeUnit: boolean;

  /**
   * If true, the scale will be rendered in dark mode.
   */
  dark?: boolean;
};

/**
 * Vertical color scale line indicator of the min and max color deviations.
 *
 *  -- max
 *  -
 *  -  center
 *  -
 *  -- min
 *
 * The positions of the horizontal lines match the color bar ratios.
 *
 * @returns Color scale line indicator
 */
function ColorScaleLineIndicatorVertical({
  maxValue,
  minValue,
  dark,
  useLargeUnit,
}: VerticalColorScaleLineIndicatorProps): JSX.Element {
  const distanceUnit = useDistanceUnit();

  const labels = useMemo(() => {
    const step = (maxValue - minValue) / 4;
    return [
      formatLabelText(maxValue, distanceUnit, useLargeUnit),
      formatLabelText(maxValue - step, distanceUnit, useLargeUnit),
      formatLabelText(minValue + 2 * step, distanceUnit, useLargeUnit),
      formatLabelText(minValue + step, distanceUnit, useLargeUnit),
      formatLabelText(minValue, distanceUnit, useLargeUnit),
    ];
  }, [distanceUnit, maxValue, minValue, useLargeUnit]);

  const styleGrade = { opacity: 0.3 };

  const labelColor = dark ? grey[0] : grey[900];

  return (
    <Stack direction="row" sx={{ height: "250px" }}>
      {/** graduated line */}
      <Stack
        direction="column"
        sx={{ alignItems: "center", justifyContent: "center", ...styleGrade }}
      >
        <Box component="div" sx={{ height: "15%", width: "1px" }} />
        <Box
          component="div"
          sx={{ height: "1px", width: "10px", bgcolor: labelColor }}
        />
        <Box component="div" sx={{ height: "17%", width: "1px" }} />
        <Box
          component="div"
          sx={{ height: "1px", width: "8px", bgcolor: labelColor }}
        />
        <Box component="div" sx={{ height: "17%", width: "1px" }} />
        <Box
          component="div"
          sx={{ height: "1px", width: "8px", bgcolor: labelColor }}
        />
        <Box component="div" sx={{ height: "17%", width: "1px" }} />
        <Box
          component="div"
          sx={{ height: "1px", width: "8px", bgcolor: labelColor }}
        />
        <Box component="div" sx={{ height: "17%", width: "1px" }} />
        <Box
          component="div"
          sx={{ height: "1px", width: "10px", bgcolor: labelColor }}
        />
        <Box component="div" sx={{ height: "15%", width: "1px" }} />
      </Stack>

      {/** labels */}
      <Stack
        direction="column"
        spacing={4}
        sx={{ alignItems: "center", justifyContent: "center", margin: "4px" }}
      >
        {labels.map((label, index) => (
          <FaroText
            variant="bodyXS"
            className={NO_TRANSLATE_CLASS}
            color={labelColor}
            key={index}
            sx={{
              opacity:
                !dark || index === 0 || index === labels.length - 1 ? 0.5 : 1,
            }}
          >
            {label}
          </FaroText>
        ))}
      </Stack>
    </Stack>
  );
}
